<template>
  <div
    class="card border-0 profile-card"
    :class="isDarkMode ? 'profile-card-dark' : 'profile-card-light'"
  >
    <div class="profile-card-inner">
      <div class="profile-avatar-wrap">
        <img
          v-if="avatarSrc"
          :src="avatarSrc"
          class="profile-avatar"
          :class="{ 'is-clickable': secured }"
          alt="player"
          @click="changeImage"
        >
        <button
          v-if="secured"
          type="button"
          class="profile-camera-btn"
          title="Změnit fotku"
          @click="emit('change-image')"
        >
          <fa-icon icon="fa-solid fa-camera" />
        </button>
      </div>

      <h5 class="profile-nick mb-1">{{ user?.UserNickName }}</h5>
      <p v-if="secured && user?.UserName" class="profile-name mb-3">{{ user.UserName }}</p>

      <div v-if="secured && (user?.UserEmail || user?.UserPhone)" class="profile-meta">
        <div v-if="user?.UserEmail" class="profile-meta-card">
          <span class="profile-meta-icon"><fa-icon icon="fa-solid fa-envelope" /></span>
          <div class="profile-meta-text">
            <div class="profile-meta-label">E-mail</div>
            <div class="profile-meta-value text-truncate" :title="user.UserEmail">{{ user.UserEmail }}</div>
          </div>
        </div>
        <div v-if="user?.UserPhone" class="profile-meta-card">
          <span class="profile-meta-icon"><fa-icon icon="fa-solid fa-phone" /></span>
          <div class="profile-meta-text">
            <div class="profile-meta-label">Telefon</div>
            <div class="profile-meta-value">{{ formatPhone(user.UserPhone) }}</div>
          </div>
        </div>
      </div>

      <button
        v-if="secured"
        type="button"
        class="profile-pass-btn"
        @click="store.commit('toggleChangePasswordForm')"
      >
        <fa-icon icon="fa-solid fa-key" />
        Změnit heslo
      </button>
      <button
        v-if="secured"
        type="button"
        class="profile-cancel-btn"
        @click="emit('cancel-account')"
      >
        Zrušit účet
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { userAvatarUrl } from "@/helpers/userAvatars";

const props = defineProps({
  user: {
    type: [Array, Object]
  },
  secured: {
    type: Boolean,
    default: true
  }
});
const emit = defineEmits(["change-image", "cancel-account"]);

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const avatarSrc = computed(() => userAvatarUrl(
  props.user?.UserImage,
  store.state.imageCacheKey,
  !!(props.user?.UserID || props.user?.UserNickName)
));

const changeImage = () => {
  if (props.secured) emit("change-image");
};

const formatPhone = (raw) => {
  const digits = String(raw ?? "").replace(/\D/g, "");
  let n = digits;
  if (n.startsWith("420") && n.length >= 12) n = n.slice(3);
  if (n.length === 9) return `${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6, 9)}`;
  return String(raw ?? "");
};
</script>

<style scoped>
.profile-card-light {
  background: #ffffff;
  border: 1px solid #E2E8F0 !important;
  border-radius: 0.85rem !important;
}

.profile-card-dark {
  background: #0F172A !important;
  border: 1px solid #334155 !important;
  border-radius: 0.85rem !important;
  box-shadow: none !important;
}

.profile-card-inner {
  padding: 1.35rem 1.15rem 1.2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.profile-avatar-wrap {
  position: relative;
  width: 112px;
  height: 112px;
  margin-bottom: 1rem;
  border-radius: 50%;
  background: #F1F5F9;
  border: 3px solid #FF8A4C;
  box-sizing: border-box;
}

.profile-card-dark .profile-avatar-wrap {
  background: #1E293B;
}

.profile-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: none;
  background: #F1F5F9;
}

.profile-card-dark .profile-avatar {
  background: #1E293B;
}

.profile-avatar.is-clickable {
  cursor: pointer;
}

.profile-camera-btn {
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: #FF8A4C;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.18);
}

.profile-nick {
  font-weight: 800;
  font-size: 1.15rem;
  color: #0F172A;
  margin: 0;
}

.profile-name {
  font-size: 0.85rem;
  color: #64748B;
}

.profile-card-dark .profile-nick {
  color: #E2E8F0;
}

.profile-card-dark .profile-name {
  color: #94A3B8;
}

.profile-meta {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.profile-meta-card:only-child {
  grid-column: 1 / -1;
}

.profile-meta-card {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  text-align: left;
  padding: 0.55rem 0.6rem;
  border-radius: 0.65rem;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.profile-card-dark .profile-meta-card {
  background: #1E293B;
  border-color: #334155;
}

.profile-meta-text {
  min-width: 0;
  flex: 1 1 auto;
}

.profile-meta-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94A3B8;
  line-height: 1.1;
  margin-bottom: 0.12rem;
}

.profile-meta-value {
  font-size: 0.78rem;
  font-weight: 700;
  color: #334155;
  line-height: 1.2;
}

.profile-card-dark .profile-meta-value {
  color: #E2E8F0;
}

.profile-meta-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #FFF4EC;
  color: #FF8A4C;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.72rem;
}

.profile-card-dark .profile-meta-icon {
  background: rgba(255, 138, 76, 0.14);
}

.profile-pass-btn {
  border: 1.5px solid #FF8A4C;
  background: transparent;
  color: #FF8A4C;
  border-radius: 999px;
  padding: 0.4rem 0.95rem;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.profile-pass-btn:hover {
  background: rgba(255, 138, 76, 0.12);
}

.profile-cancel-btn {
  margin-top: 0.7rem;
  border: none;
  background: transparent;
  color: #94A3B8;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.profile-cancel-btn:hover {
  color: #DC3545;
}
</style>
